import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import SelectDocumentInGroup from '~/components/SearchV2/AddSearchResultsToGroupV2/SelectDocumentInGroup/SelectDocumentInGroup';
import SelectGroup from '~/components/SearchV2/AddSearchResultsToGroupV2/SelectGroup/SelectGroup';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { AddToGroupStepType } from '~/enums/AddToGroupType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { SearchQuery } from '~/features/advanced-search/types';
import { api } from '~/redux/api/createApi';
import { PilotSearchQuery } from '~/redux/api/methods/search';
import { coerceRtkqError } from '~/redux/api/utils';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';

const MAX_TITLE_WIDTH = 355;

type Props = {
  query: PilotSearchQuery | SearchQuery;
  tableSelection: ReturnType<typeof useTableSelection>;
  onHide: () => void;
  onCompleted: () => void;
};

export const AddSearchResultsToGroup = ({
  query: pilotSearchQuery,
  tableSelection,
  onHide,
  onCompleted,
}: Props) => {
  const { selectedIds, isAllSelected } = tableSelection;
  const intl = useIntl();
  const [step, setStep] = useState<AddToGroupStepType>(
    AddToGroupStepType.GROUP,
  );
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(
    undefined,
  );
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    number | undefined
  >(undefined);

  const [addDocumentsToGroup, addDocumentsToGroupResult] =
    api.endpoints.addDocumentsToGroup.useMutation();
  const {
    isError: addDocumentsToGroupError,
    isSuccess: addDocumentsToGroupSuccess,
    isLoading: addDocumentsToGroupLoading,
    error: addDocumentsToGroupResponseError,
  } = addDocumentsToGroupResult;

  const [addDocumentsToGroupLinkParent, addDocumentsToGroupLinkParentResult] =
    api.endpoints.addDocumentsToGroupLinkParent.useMutation();
  const {
    isError: addDocumentsToGroupLinkParentError,
    isSuccess: addDocumentsToGroupLinkParentSuccess,
    isLoading: addDocumentsToGroupLinkParentLoading,
    error: addDocumentsToGroupLinkParentResponseError,
  } = addDocumentsToGroupLinkParentResult;

  const [createDocumentGroup, createDocumentGroupResult] =
    api.endpoints.createDocumentGroup.useMutation();
  const {
    isError: createDocumentGroupError,
    isSuccess: createDocumentGroupSuccess,
    isLoading: createDocumentGroupLoading,
    error: createDocumentGroupResponseError,
  } = createDocumentGroupResult;

  const { toast } = useToast();
  const documentIds = isAllSelected
    ? undefined
    : selectedIds.map((id) => Number(id));
  const query = isAllSelected ? pilotSearchQuery : undefined;

  useEffect(() => {
    if (
      addDocumentsToGroupSuccess ||
      addDocumentsToGroupLinkParentSuccess ||
      createDocumentGroupSuccess
    ) {
      toast({
        status: 'success',
        message: intl.formatMessage({
          id: 'search.AddSearchResultsToGroup.successMessage',
          defaultMessage: 'Documents have been added to the group successfully',
          description: 'Success message when documents are added to group',
        }),
      });
      onCompleted();
    }
  }, [
    addDocumentsToGroupSuccess,
    addDocumentsToGroupLinkParentSuccess,
    createDocumentGroupSuccess,
    intl,
  ]);

  useEffect(() => {
    if (
      addDocumentsToGroupError ||
      addDocumentsToGroupLinkParentError ||
      createDocumentGroupError
    ) {
      const error =
        addDocumentsToGroupResponseError ||
        addDocumentsToGroupLinkParentResponseError ||
        createDocumentGroupResponseError;
      const { response } = coerceRtkqError(error);
      const defaultErrorMessage = intl.formatMessage({
        id: 'search.AddSearchResultsToGroup.errorMessage',
        defaultMessage:
          'An error occurred while adding documents to the group.',
        description: 'Error message when adding documents to group fails',
      });
      if (response?.status === HttpStatusCodeType.BadRequest) {
        toast({
          status: 'danger',
          message: get(response, 'data.message', defaultErrorMessage),
        });
      } else {
        toast({
          status: 'danger',
          message: defaultErrorMessage,
        });
      }
      onHide();
    }
  }, [
    addDocumentsToGroupError,
    addDocumentsToGroupLinkParentError,
    createDocumentGroupError,
    intl,
  ]);

  const handleSelectGroup = useCallback((groupId: number) => {
    setStep(AddToGroupStepType.STRUCTURE);
    setSelectedGroupId(groupId);
  }, []);

  const handleNewGroupCreated = ({
    name,
    note,
  }: {
    name: string;
    note: string;
  }) => {
    createDocumentGroup({
      name,
      note,
      documents: documentIds,
      query_v3: query as SearchQuery,
    });
  };

  const handleSelectedDocument = useCallback((documentId: number) => {
    setSelectedDocumentId(documentId);
  }, []);

  const handleAddDocumentsToGroup = () => {
    if (selectedDocumentId && selectedGroupId) {
      addDocumentsToGroupLinkParent({
        groupId: selectedGroupId,
        parentId: selectedDocumentId,
        documentIds,
        query_v3: query as SearchQuery,
      });
    } else {
      if (selectedGroupId) {
        addDocumentsToGroup({
          groupId: selectedGroupId,
          documentIds,
          query_v3: query as SearchQuery,
        });
      }
    }
    setSelectedDocumentId(undefined);
    setSelectedGroupId(undefined);
  };

  const steps: Record<any, any> = useMemo(() => {
    return {
      [AddToGroupStepType.GROUP]: {
        title: intl.formatMessage({
          id: 'search.AddSearchResultsToGroup.addingDocumentTitle',
          defaultMessage: 'Adding document to group',
          description: 'Title for adding document to group step',
        }),
        component: (
          <SelectGroup
            onSelectGroup={handleSelectGroup}
            onNewGroupCreated={handleNewGroupCreated}
          />
        ),
        action: {
          promise: onHide,
          text: intl.formatMessage({
            id: 'search.AddSearchResultsToGroup.cancelButton',
            defaultMessage: 'Cancel',
            description: 'Cancel button text',
          }),
          variant: 'ghost',
        },
      },
      [AddToGroupStepType.STRUCTURE]: {
        title: intl.formatMessage({
          id: 'search.AddSearchResultsToGroup.addToGroupTitle',
          defaultMessage: 'Add to group',
          description: 'Title for add to group step',
        }),
        component: (
          <SelectDocumentInGroup
            group={selectedGroupId}
            documentGroupIds={selectedIds}
            onDocumentSelected={handleSelectedDocument}
          />
        ),
        action: {
          text: intl.formatMessage({
            id: 'search.AddSearchResultsToGroup.addToGroupButton',
            defaultMessage: 'Add to group',
            description: 'Add to group button text',
          }),
        },
      },
    };
  }, [selectedGroupId]);

  const currentStep = steps[step];

  const isLoading =
    addDocumentsToGroupLoading ||
    addDocumentsToGroupLinkParentLoading ||
    createDocumentGroupLoading;

  const buttonTitle = selectedDocumentId
    ? intl.formatMessage({
        id: 'search.AddSearchResultsToGroup.addAsLinkedButton',
        defaultMessage: 'Add to group as linked documents',
        description: 'Button text for adding as linked documents',
      })
    : currentStep.action.text;
  const handleActionPromise =
    step === AddToGroupStepType.STRUCTURE
      ? handleAddDocumentsToGroup
      : currentStep.action.promise;
  return (
    <WizardPageLayout
      currentStep={step}
      steps={[AddToGroupStepType.GROUP, AddToGroupStepType.STRUCTURE] as any}
      title={{ text: currentStep.title, maxWidth: MAX_TITLE_WIDTH }}
      onClose={onHide}
      actionButton={{
        ...currentStep.action,
        text: buttonTitle,
        promise: handleActionPromise,
      }}
      isContentFill={true}
      rightContent={undefined}
    >
      <LoadingContainer isLoading={isLoading}>
        {currentStep.component}
      </LoadingContainer>
    </WizardPageLayout>
  );
};
